import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnnouncementAdd, AnnouncementDelete, AnnouncementUpdate, fetchAnnouncements } from "./announcements.api";
import { keys } from "./keys";
import useUserStore from "../stores/user-store";

const sortAnnouncements = (items = []) => {
    return [...items].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
};

const cloneItem = (item) => ({ ...item });

const normalizeAnnouncement = (item) => ({
    ...item,
    goodUntilDate: item.goodUntilDate || null
});

const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = `${today.getMonth() + 1}`.padStart(2, "0");
    const day = `${today.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const isExpired = (goodUntilDate) => {
    if (!goodUntilDate) {
        return false;
    }

    return goodUntilDate < getTodayString();
};

const emptyAnnouncementResponse = {
    activeAnnouncements: [],
    expiredAnnouncements: []
};

const setAnnouncementCaches = (qc, userId, announcement, mode, replaceId = null) => {
    const listKey = keys.announcements.list();
    const normalized = normalizeAnnouncement(announcement);
    const goesToExpired = normalized.createdByUserId === userId && isExpired(normalized.goodUntilDate);

    qc.setQueryData(listKey, (old = emptyAnnouncementResponse) => {
        const activeAnnouncements = old.activeAnnouncements ?? [];
        const expiredAnnouncements = old.expiredAnnouncements ?? [];
        const nextActive = activeAnnouncements.filter((item) => item.id !== normalized.id && item.id !== replaceId);
        const nextExpired = expiredAnnouncements.filter((item) => item.id !== normalized.id && item.id !== replaceId);

        if (mode === "delete") {
            return {
                activeAnnouncements: sortAnnouncements(nextActive),
                expiredAnnouncements: sortAnnouncements(nextExpired)
            };
        }

        return {
            activeAnnouncements: goesToExpired ? sortAnnouncements(nextActive) : sortAnnouncements([...nextActive, cloneItem(normalized)]),
            expiredAnnouncements: goesToExpired ? sortAnnouncements([...nextExpired, cloneItem(normalized)]) : sortAnnouncements(nextExpired)
        };
    });
};

export const useAnnouncementsQuery = () => {
    return useQuery({
        queryKey: keys.announcements.list(),
        queryFn: fetchAnnouncements,
        placeholderData: (prev) => prev
    });
};

export const useAnnouncementAdd = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();
    const userId = useUserStore((state) => state.userid);
    const username = useUserStore((state) => state.username);

    return useMutation({
        mutationFn: AnnouncementAdd,
        onMutate: async(newItem) => {
            await qc.cancelQueries({ queryKey: keys.announcements.list() });

            const previousData = qc.getQueryData(keys.announcements.list());
            const now = new Date().toISOString();
            const optimisticId = `optimistic-${crypto.randomUUID()}`;
            const optimisticItem = {
                id: optimisticId,
                title: newItem.title.trim(),
                body: newItem.body.trim(),
                goodUntilDate: newItem.goodUntilDate || null,
                createdAt: now,
                updatedAt: now,
                createdByUserId: userId ?? 0,
                createdByUsername: username ?? "current.user"
            };

            setAnnouncementCaches(qc, userId, optimisticItem, "upsert");

            return { previousData, optimisticId };
        },
        onError: (error, _vars, context) => {
            if (context) {
                qc.setQueryData(keys.announcements.list(), context.previousData);
            }
            if (errorCallback) {
                errorCallback(error);
            }
        },
        onSuccess: (data, _vars, context) => {
            setAnnouncementCaches(qc, userId, data, "upsert", context?.optimisticId ?? null);
            if (successCallback) {
                successCallback(data);
            }
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.announcements.list() });
        }
    });
};

export const useAnnouncementUpdate = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();
    const userId = useUserStore((state) => state.userid);

    return useMutation({
        mutationFn: AnnouncementUpdate,
        onMutate: async(updatedItem) => {
            await qc.cancelQueries({ queryKey: keys.announcements.list() });

            const previousData = qc.getQueryData(keys.announcements.list());
            const currentItem =
                (previousData?.activeAnnouncements ?? []).find((item) => item.id === updatedItem.id) ??
                (previousData?.expiredAnnouncements ?? []).find((item) => item.id === updatedItem.id);

            if (currentItem) {
                setAnnouncementCaches(qc, userId, {
                    ...currentItem,
                    ...updatedItem,
                    updatedAt: new Date().toISOString()
                }, "upsert");
            }

            return { previousData };
        },
        onError: (error, _vars, context) => {
            if (context) {
                qc.setQueryData(keys.announcements.list(), context.previousData);
            }
            if (errorCallback) {
                errorCallback(error);
            }
        },
        onSuccess: (data) => {
            setAnnouncementCaches(qc, userId, data, "upsert");
            if (successCallback) {
                successCallback(data);
            }
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.announcements.list() });
        }
    });
};

export const useAnnouncementDelete = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();
    const userId = useUserStore((state) => state.userid);

    return useMutation({
        mutationFn: AnnouncementDelete,
        onMutate: async(deleteItem) => {
            await qc.cancelQueries({ queryKey: keys.announcements.list() });

            const previousData = qc.getQueryData(keys.announcements.list());
            const currentItem =
                (previousData?.activeAnnouncements ?? []).find((item) => item.id === deleteItem.id) ??
                (previousData?.expiredAnnouncements ?? []).find((item) => item.id === deleteItem.id);

            if (currentItem) {
                setAnnouncementCaches(qc, userId, currentItem, "delete");
            }

            return { previousData };
        },
        onError: (error, _vars, context) => {
            if (context) {
                qc.setQueryData(keys.announcements.list(), context.previousData);
            }
            if (errorCallback) {
                errorCallback(error);
            }
        },
        onSuccess: (data) => {
            setAnnouncementCaches(qc, userId, data, "delete");
            if (successCallback) {
                successCallback(data);
            }
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.announcements.list() });
        }
    });
};
